import React from 'react';

import ItemSelector from '@components/item-selector/ItemSelector';
import SearchableDropdown from '@components/searchable-dropdown/SearchableDropdown';
import EditSourceModal from '@features/lead-management/components/edit-source-modal/EditSourceModal';
import { SourceSelectItem } from '@features/lead-management/hooks/useLeadSources';
import useNotify from '@hooks/notify';

interface SourceInputProps {
  value: string;
  onChange: (value: string) => void;
  onSourceChange: (source: SourceSelectItem | null) => void;
  selectedSource: SourceSelectItem | null;
  sourceOptions?: SourceSelectItem[];
  onUpdateSource?: (source: SourceSelectItem) => void;
  nonEditableSources?: string[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowCreate?: boolean;
  positionFixed?: boolean;
}

const SourceSelectorWrapper = (props: any) => (
  <ItemSelector<SourceSelectItem>
    {...props}
    showColorSelector={false}
    showEditButton
    createButtonText="Create"
    noItemsText="No sources found"
    EditModalComponent={EditSourceModal}
    itemType="source"
    alwaysShowCreate
    disableFilter
    hideCreateWhenExists
    createIdleBgColor="#F7F9FC"
    createActiveBgColor="#F0F2F5"
    createButtonClassName="h-[45px]"
    itemListClassName="!gap-0"
    itemRowClassName="!mx-0 !my-0 !h-[45px] !rounded-none !py-3 !pl-4"
  />
);

const SourceInput = ({
  value,
  onChange,
  onSourceChange,
  selectedSource,
  sourceOptions = [],
  onUpdateSource,
  nonEditableSources = [],
  placeholder = 'Select or search source',
  className = '',
  disabled = false,
  allowCreate = true,
  positionFixed = false
}: SourceInputProps) => {
  const { createErrorAlert } = useNotify();

  const handleCreateSource = (sourceName: string): SourceSelectItem => ({
    id: `custom-source-${Date.now()}`,
    name: sourceName
  });

  const handleSourceChange = (sources: SourceSelectItem[]) => {
    onSourceChange(sources.length > 0 ? sources[0] : null);
  };

  const validateNameLength = (name: string) => {
    if (name.length < 2 || name.length > 50) {
      createErrorAlert('Source name must be between 2 and 50 characters');
      return false;
    }
    return true;
  };

  const selectedSources = selectedSource ? [selectedSource] : [];

  return (
    <SearchableDropdown<SourceSelectItem>
      value={value}
      onChange={onChange}
      onItemsChange={handleSourceChange}
      selectedItems={selectedSources}
      availableItems={sourceOptions}
      onUpdateItem={onUpdateSource}
      onCreateItem={allowCreate ? handleCreateSource : undefined}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      allowCreate={allowCreate}
      allowMultiple={false}
      openOnFocus
      SelectorComponent={SourceSelectorWrapper}
      validateNameLength={validateNameLength}
      selectorProps={{
        nonEditableFields: nonEditableSources,
        positionFixed
      }}
    />
  );
};

export default SourceInput;
